import * as React from 'react';
import { resolve } from 'inversify-react';
import { observable, computed } from 'mobx';
import { observer } from 'mobx-react';
import autobind from 'autobind-decorator';
import { connect } from "react-redux";
import { Dispatch } from "redux";

import { ToastNotifications } from 'src/core/ui/toast';
import { ControllerProps } from 'src/router';
import { ObjectUtils } from 'src/core/utils/object';
import { ProductFormView } from './product-form.view';
import { ProductDataService } from './product-form.data';
import { ContextService } from 'src/core/services/context.service';
import { FormatService } from 'src/core/services/format.service';
import { ConfigurationService } from 'src/core/services/configuration.service';
import { IdentityService } from 'src/core/services/identity.service';
import { EntitySchemaDataService } from 'src/app/general/entity-schemas/entity-schema.data';
import { actionCreators } from "src/store/products/product-form-nav-actions";
import { actionAvailabilitiesCreators } from "src/store/allotments/availabilities-actions";

interface PropsFromState {
	breadcrumb: ReadonlyArray<Readonly<BreadcrumbItem<ProductFormItemType>>>;
	otherLoadedEntities: ReadonlyArray<Readonly<{
		entity: ProductFormItemType
	}>>;
}

interface PropsFromDispatch {
	clearStore: () => any;
	clearAvailabilitiesStore: () => any;
}

interface Props extends ControllerProps<{ id: string; type?: string }> {
}

interface State {
	mode: ControlMode;
	schema?: JsonSchema;
	productCodeRequired?: boolean;
	defaultLanguage?: string;
	allotmentNew?: AllotmentItem,
}

@observer
class ProductFormController extends React.Component<Props & PropsFromDispatch & PropsFromState, State> {
	@resolve
	identityService: IdentityService;

	@resolve
	contextService: ContextService;

	@resolve
	formatService: FormatService;

	@resolve
	toast: ToastNotifications;

	@resolve
	configurationService: ConfigurationService;

	@observable
	dataService: ProductDataService;

	constructor(props: Readonly<Props & PropsFromDispatch & PropsFromState>) {
		super(props);

		this.state = {
			mode: 'display'
		};
	}

	
	componentDidMount() {
		this.props.setBusy(true);
		this.dataService = ProductDataService.make(this.contextService);

		this.loadConfiguration()
			.then(() => Promise.all([this.loadData(), this.loadSchema()]))
			.catch((error) => {
				this.toast.showToast('Request failed', ObjectUtils.parseError(error), 'error');
			})
			.then(() => {
				this.props.setBusy(false);
			});
	}

	public componentWillUnmount() {
		this.props.clearStore();
		this.props.clearAvailabilitiesStore();
	}

	private async loadConfiguration(): Promise<void> {
		const appConfiguration = await this.configurationService.current();
		this.setState({
			productCodeRequired: appConfiguration.productCodeRequired,
			defaultLanguage: appConfiguration.defaultLanguage
		});
		this.dataService.productCodeRequired = appConfiguration.productCodeRequired;
	}

	private async loadData(): Promise<void> {
		if (this.props.match.params.id == 'new') {
			// Create new
			const { defaultLanguage } = this.state;
			await this.dataService.add(this.props.match.params.type, defaultLanguage);
			this.handleModeChange('edit');
		} else {
			// Load entity
			this.dataService.addFilter('id', 'eq', this.props.match.params.id);
			this.dataService.addSorting('id', 'asc', true);
			this.dataService.pageSize = 1;
			this.dataService.load(query => query);
		}
	}

	private async loadSchema(): Promise<void> {
		const entitySchemaService = EntitySchemaDataService.make();

		entitySchemaService.addFilter('name', 'eq', 'Product');
		entitySchemaService.addSorting('name', 'asc', true);
		entitySchemaService.pageSize = 1;

		const entitySchemas = await entitySchemaService.load(query => query);
		const schema = ObjectUtils.toObject<JsonSchema>(entitySchemas[0].schema);
		this.setState({ schema: schema });
	}

	private prepareForSave(product: ProductItem) {
		const { otherLoadedEntities } = this.props;
		const productInStore: ProductItem = otherLoadedEntities.find(e => e.entity == product.entity) as any;
		if (productInStore.contracts != null) {
			for (const c of productInStore.contracts) {
				this.prepareContractForSave(product, c);
			}
		}

		if (productInStore.childrenProducts != null) {
			for (const p of productInStore.childrenProducts) {
				this.prepareForSave(p);
			}
		}

		if (productInStore.itineraries) {
			for (const itinerary of productInStore.itineraries) {
				this.prepareItineraryForSave(product, itinerary);
			}
		}
	}

	private prepareItineraryForSave(product: ProductItem, itinerary: TourItineraryItem): void {
		const { otherLoadedEntities } = this.props;
		const productInStore: TourItineraryItem = otherLoadedEntities.find(e => e.entity == itinerary.entity) as any;

		if (productInStore.products) {
			for (let prod of productInStore.products) {
				if (itinerary.id < 0) {
					prod.entity.tourItinerary = itinerary.entity;
					prod.tourItineraryId = undefined;
				}
			}
		}

		if (productInStore.contracts) {
			for (let contract of productInStore.contracts) {
				this.prepareContractForSave(itinerary, contract);
			}
		}

		const itineraryDates = itinerary.dates || [];
		for (let date of itineraryDates) {
			if (date.id == null || date.id < 0) {
				date.id = undefined;
				if (itinerary.id < 0) {
					date.entity.tourItinerary = itinerary.entity;
					date.tourItineraryId = undefined;
				}
			}
		}

		if (itinerary.id == null || itinerary.id < 0) {
			itinerary.id = undefined;
			if (itinerary.productId < 0) {
				itinerary.entity.product = product.entity;
				itinerary.productId = undefined;
			}
		}
	}

	private prepareContractForSave(product: ProductItem | TourItineraryItem, c: ContractItem) {
		const { otherLoadedEntities } = this.props;
		const contract: ContractItem = otherLoadedEntities.find(e => e.entity == c.entity) as any;

		if (contract.markets) {
			for (const market of contract.markets) {
				if (market.id == null || market.id < 0) {
					market.id = undefined;
					if (c.id < 0) {
						market.entity.contract = c.entity;
						market.contractId = undefined;
					}
				}
			}
		}

		if (contract.allotment != null) {
			const { otherLoadedEntities } = this.props;
			const inStore: AllotmentItem = otherLoadedEntities.find(e => e.entity == contract.allotment.entity) as any;

			if (inStore.allotmentRanges) {
				for (let prod of inStore.allotmentRanges) {
					const inStore: AllotmentRangeItem = otherLoadedEntities.find(e => e.entity == prod.entity) as any;
					if (inStore.allotmentRangeDailies) {
						for (const item of inStore.allotmentRangeDailies) {
							if (prod.id < 0) {
								item.entity.allotmentRange = prod.entity;
								item.allotmentRangeId = undefined;
							}
							if (item.id == null || item.id < 0)
								item.id = undefined;
						}
					}
					if (contract.allotment.id < 0) {
						prod.entity.allotment = contract.allotment.entity;
						prod.allotmentId = undefined;
					}
				}
			}
			if (contract.allotment.id < 0) {
				contract.entity.allotment = contract.allotment.entity;
				contract.allotment.id = undefined;
				contract.entity.allotmentId = undefined;
			}
		}

		if (contract.dateBands) {
			for (const dateBand of contract.dateBands) {
				if (dateBand.id == null || dateBand.id < 0) {
					dateBand.id = undefined;
					if (c.id < 0) {
						dateBand.entity.contract = c.entity;
						dateBand.contractId = undefined;
					}
				}
			}
		}

		if (contract.services) {
			for (const service of contract.services) {
				if (service.id == null || service.id < 0) {
					service.id = undefined;
					if (c.id < 0) {
						service.entity.contract = c.entity;
						service.contractId = undefined;
					}
				}
			}
		}

		if (c.id == null || c.id < 0) {
			c.id = undefined;
			if (c.productId < 0 && c.tourItineraryId == null) {
				c.entity.product = product.entity;
				c.productId = undefined;
			}
			if (c.productId == null && c.tourItineraryId < 0) {
				c.entity.tourItinerary = product.entity;
				c.tourItineraryId = undefined;
			}
		}
	}

	@computed
	get title(): string {
		if (this.dataService && this.dataService.item && this.dataService.item.dynamicProperties['displayName']) {
			return `Product ${this.dataService.item.dynamicProperties['displayName'][this.state.defaultLanguage]} `;
		}
		return '';
	}

	@autobind
	private handleModeChange(mode: ControlMode): void {
		this.setState({ mode: mode });
	}

	@autobind
	private async handleSave(): Promise<void> {
		const item = this.dataService.item;
		const errors = await item.validate(true);
		if (errors.length == 0) {
			this.props.setBusy(true);
			this.prepareForSave(this.dataService.item);
			this.dataService.save().then(n => {
				this.props.setBusy(false);
				this.toast.showDataSaved();
				if (this.props.match.params.id === "new") {
					this.props.history.replace(`/products/products/${this.dataService.item.entity.id}`);
				}
			}).catch(error => {
				this.props.setBusy(false);
				this.toast.showToast('Request failed', ObjectUtils.parseError(error), 'error');
			});
		} else {
			this.toast.showToast('Validation error!', errors.map(e => e.text), 'error');
		}
	}

	@autobind
	private handleCancel(): void {
		this.props.history.push('/products/products');
	}

	render(): React.ReactNode {
		const { defaultLanguage, productCodeRequired, schema } = this.state;
		if (this.dataService == null || this.dataService.item == null || schema == null) {
			return null;
		}
		if (this.dataService.item.dynamicProperties['erratas'] == null) {
			this.dataService.item.dynamicProperties['erratas'] = [];
		}

		return (
			<ProductFormView
				identityService={this.identityService}
				toast={this.toast}
				title={this.title}
				mode={this.state.mode}
				onModeChanged={this.handleModeChange}
				cancel={this.handleCancel}
				save={this.handleSave}
				model={this.dataService.item}
				contextService={this.contextService}
				formatService={this.formatService}
				setBusy={this.props.setBusy}
				productCodeRequired={productCodeRequired}
				defaultLanguage={defaultLanguage}
				schema={schema}
				breadcrumb={this.props.breadcrumb}
			/>
		);
	}
}

const mapStateToProps = (state: IApplicationState): PropsFromState => ({
	breadcrumb: state.productFormBreadcrumb,
	otherLoadedEntities: state.otherLoadedEntities,
});

const mapDispatchToProps = (dispatch: Dispatch): PropsFromDispatch => ({
	clearStore: () => dispatch(actionCreators.clearStore()),
	clearAvailabilitiesStore: () => dispatch(actionAvailabilitiesCreators.clearStore()),

});

const connected = connect(mapStateToProps, mapDispatchToProps)(ProductFormController);
export { connected as ProductFormController };



// WEBPACK FOOTER //
// ./src/app/products/products/form/product-form.controller.tsx